/**
 * Freelance projects, seen from both sides: the freelancer submits milestones, the company or startup reviews them.
 * VConnect records the agreed amounts but does not hold or move the money, and says so on every project.
 */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html, safeUrl } from '../../core/html.js';
import { fmtDate, fmtDateTime, inr } from '../../core/format.js';
import { reload } from '../../app/shell.js';
import { backLink, banner, empty, ic, pageHead, statusTag, table } from '../../ui/components.js';
import { field } from '../../ui/forms.js';
import { closeModal, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';

const SIDE = {
  professional: { list: '/freelancer/projects', one: (id) => `/freelancer/projects/${id}`, other: 'Client', otherKey: 'client_name' },
  company: { list: '/org/projects', one: (id) => `/org/projects/${id}`, other: 'Freelancer', otherKey: 'freelancer_name' },
  startup: { list: '/org/projects', one: (id) => `/org/projects/${id}`, other: 'Freelancer', otherKey: 'freelancer_name' },
};

const NOTE = 'Amounts below are what you both agreed. VConnect records progress but does not hold or move the money, so settle payments directly.';

function listRoute(role) {
  const side = SIDE[role];
  return {
    path: `/${role}/projects`,
    roles: [role],
    title: 'Projects',
    load: (ctx) => api.get(side.list, { signal: ctx.signal }),
    view(d) {
      return html`${pageHead('Projects', 'Work that has started, with its milestones.')}
        ${d.projects.length
          ? table(['Project', side.other, 'Agreed total', 'Status', ''], d.projects.map((p) => [p.title, p[side.otherKey], inr(p.budget_paise), statusTag(p.status), html`<a class="btn ghost sm" href="/${role}/projects/${p.id}">Open</a>`]))
          : empty('layers', 'No projects yet', role === 'professional' ? 'When a client hires you from an application, the project appears here.' : 'Hire a freelancer from your requirements to start a project.')}
        <p class="note mt-s">${ic('info')} ${NOTE}</p>`;
    },
  };
}

function nextOpen(milestones) {
  return milestones.find((m) => m.status !== 'approved');
}

function milestoneCard(m, role, project, openOne) {
  const open = openOne && openOne.id === m.id && project.status !== 'completed' && project.status !== 'cancelled';
  return html`<div class="card mb" id="ms-${m.id}"><div class="row between"><div><span class="mu xs">Milestone ${m.position + 1}</span><h3 style="margin:2px 0 0">${m.title}</h3></div>
      <div style="text-align:right"><b>${inr(m.amount_paise)}</b><div>${statusTag(m.status)}</div></div></div>
    ${m.submitted_at ? html`<p class="sm mt-s">Submitted ${fmtDateTime(m.submitted_at)}.${m.status === 'submitted' && m.auto_approve_at ? ` If not reviewed it is approved automatically on ${fmtDate(m.auto_approve_at)}.` : ''}</p>` : ''}
    ${m.link ? html`<p class="sm"><a href="${safeUrl(m.link)}" target="_blank" rel="noopener noreferrer">${ic('link')} Open delivered work</a></p>` : ''}
    ${m.note ? html`<p class="sm mu" style="white-space:pre-line">${m.note}</p>` : ''}
    ${role === 'professional' && open && (m.status === 'pending' || m.status === 'changes')
      ? html`<button type="button" class="btn sm" data-act="ms.submit" data-id="${m.id}" data-title="${m.title}">${m.status === 'changes' ? 'Submit again' : 'Submit work'}</button>` : ''}
    ${role !== 'professional' && m.status === 'submitted'
      ? html`<div class="row"><button type="button" class="btn sm" data-act="ms.decide" data-id="${m.id}" data-action="approve" data-title="${m.title}">Approve</button>
        <button type="button" class="btn ghost sm" data-act="ms.decide" data-id="${m.id}" data-action="changes" data-title="${m.title}">Ask for changes</button></div>` : ''}</div>`;
}

function detailRoute(role) {
  const side = SIDE[role];
  return {
    path: `/${role}/projects/:id`,
    roles: [role],
    nav: 'projects',
    title: (d) => (d ? d.project.title : 'Project'),
    load: (ctx) => api.get(side.one(Number(ctx.params.id)), { signal: ctx.signal }),
    view({ project: p }) {
      const open = nextOpen(p.milestones);
      const done = p.milestones.filter((m) => m.status === 'approved').length;
      return html`${backLink(`/${role}/projects`, 'Projects')}
        <div class="page-h"><div><h2 style="margin:0 0 4px">${p.title}</h2><p>${side.other}: <b>${p[side.otherKey]}</b>. Agreed total ${inr(p.budget_paise)}. Started ${fmtDate(p.created_at)}.</p></div>${statusTag(p.status)}</div>
        ${banner(NOTE, '', 'info')}
        <p class="sm mu mt-s">${done} of ${p.milestones.length} milestones approved.</p>
        <div class="mt-s">${p.milestones.map((m) => milestoneCard(m, role, p, open))}</div>`;
    },
  };
}

register({
  'ms.submit': (el) => openModal({
    title: `Submit: ${el.dataset.title}`,
    body: html`<form id="ms-form" data-submit="ms.submit.send" data-id="${el.dataset.id}" novalidate>
      ${field('ms-link', 'Link to the work', { name: 'link', type: 'url', ph: 'https://github.com/you/repo or a shared drive link', err: 'Enter a full link starting with https://' })}
      ${field('ms-note', 'Note for the client', { name: 'note', rows: 4, req: false, max: 2000, ph: 'What is included, how to review it' })}</form>
      <p class="note">The client reviews it. If they do not respond in time it is approved automatically.</p>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="ms-form" class="btn">Submit work</button>`,
  }),
  'ms.submit.send': (form) => submitForm(form, async ({ link, note }) => {
    if (!/^https?:\/\//i.test(link)) throw new Error('The link must start with http:// or https://');
    await api.post(`/freelancer/milestones/${form.dataset.id}/submit`, { body: { link, note } });
    closeModal(false);
    toast('Work submitted for review', 'send');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),

  'ms.decide': (el) => {
    const approve = el.dataset.action === 'approve';
    openModal({
      title: approve ? `Approve: ${el.dataset.title}` : `Ask for changes: ${el.dataset.title}`,
      body: html`<form id="dec-form" data-submit="ms.decide.send" data-id="${el.dataset.id}" data-action="${el.dataset.action}" novalidate>
        ${field('dc-note', approve ? 'Note (optional)' : 'What needs to change', { name: 'note', rows: 4, req: !approve, max: 2000, minlen: approve ? 0 : 3 })}</form>
        ${approve ? html`<p class="note">Approving tells VConnect this milestone is done. Pay the freelancer directly as agreed.</p>` : ''}`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="dec-form" class="btn">${approve ? 'Approve milestone' : 'Send request'}</button>`,
    });
  },
  'ms.decide.send': (form) => submitForm(form, async ({ note }) => {
    await api.post(`/org/milestones/${form.dataset.id}/decision`, { body: { action: form.dataset.action, note } });
    closeModal(false);
    toast(form.dataset.action === 'approve' ? 'Milestone approved' : 'Changes requested', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});

export const routes = ['professional', 'company', 'startup'].flatMap((role) => [listRoute(role), detailRoute(role)]);
